import { httpCreators, HttpHelper, HttpResponse } from 'rbh-merchant-core';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { COGNITO_CHALLENGE } from '../../auth/auth-constant';
import { AuthHelper } from '../../auth/auth-helper';
import { authSagas } from '../../auth/auth-saga';
import { envConfig } from '../../config';
import { BUSINESS_TYPE, MERCHANT_TYPE } from '../../main/main-constant';
import { history } from '../../router';
import { RootState } from '../root-reducer';
import { generalCreators } from './general-action';
import { GENERAL_REQUEST_ID_LIST, MODAL_TYPE } from './general-constant';
import { GENERAL_STATE_KEY } from './general-reducer';
import { GeneralService } from './general-service';
import { GENERAL_ACTION_TYPE } from './general-type';

function* initialLoad() {
  try {
    yield call([AuthHelper, 'configure']);
    yield call(HttpHelper.createInstance, {
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Cache-Control': 'no-cache'
      },
      baseURL: envConfig.hostname
    });
    yield call([AuthHelper, 'createTokenExpirationInterceptor']);
    // if merchantTypeId and businessTypeId are undefined or invalid, user will be forced log out
    const merchantTypeId = yield call(AuthHelper.getMerchantTypeId);
    const businessTypeId = yield call(AuthHelper.getBusinessTypeId);

    if (!(merchantTypeId in MERCHANT_TYPE && businessTypeId in BUSINESS_TYPE)) {
      throw {};
    }
    // Get current session
    const status = yield select((state: RootState) => state.auth.STATUS);

    if (status !== COGNITO_CHALLENGE.LOGOUT_SUCCESS) {
      yield call(authSagas.currentSession);
    }
  } catch (e) {
    yield call(authSagas.logout);
  }
}

function* callTnc() {
  yield put(httpCreators.request({
    id: GENERAL_REQUEST_ID_LIST.GET_TNC,
    sagaHandler: getTncHandler as any,
    callbackParams: '',
    service: GeneralService.getTnc,
    serviceParams: ''
  }));
}

function* getTncHandler(resp: HttpResponse, callbackParams?: { callConFig: boolean, resetReload: () => void }) {
  if (resp.success) {
    if (callbackParams?.callConFig) {
      yield call(generalSagas.callGeneralConfig, { resetReload: callbackParams?.resetReload });
    }
    if (resp.data?.tncAcceptance.collect) {
      yield put(generalCreators.setGeneralState(GENERAL_STATE_KEY.TNC_MODAL, {
        visible: true,
        contentHtml: resp.data.tncAcceptance.contentHtml,
        tncId: resp.data.tncAcceptance.tncId
      }));
    } else {
      yield put(generalCreators.setGeneralState(GENERAL_STATE_KEY.TNC_MODAL, {
        visible: false
      }));
    }
  } else {
    yield put(generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
      visible: true,
      onOk: () => { history.push('/main'); },
      type: MODAL_TYPE.ERROR
    }));
  }
}

function* postTncHandler(resp: HttpResponse, callbackParams: { pushToMain: () => void }) {
  if (resp.success) {
    yield put(generalCreators.setGeneralState(GENERAL_STATE_KEY.TNC_MODAL, {
      visible: false
    }));
  } else {
    yield put(generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
      visible: true,
      onOk: callbackParams.pushToMain,
      type: MODAL_TYPE.ERROR
    }));
  }
}

function* callGeneralConfig(callbackParams: any) {
  yield put(httpCreators.request({
    id: GENERAL_REQUEST_ID_LIST.GET_TNC,
    sagaHandler: getGeneralConfigsHandler as any,
    callbackParams: {
      resetReload: callbackParams?.resetReload,
      configSuccess: callbackParams.configSuccess
    },
    service: GeneralService.getGeneralConfigs,
  }));
}

function* getGeneralConfigsHandler(resp: HttpResponse, callbackParams: {
  resetReload: () => void, configSuccess: (btn: any) => void
}) {
  const buttonManageEmail = resp.data?.appConfigs?.filter((config: { key: string; }) =>
    config.key === 'report.additional.email.button.enable')?.[0]?.value;
  if (resp.success) {
    yield put(generalCreators.setGeneralState(GENERAL_STATE_KEY.REMOTE_CONFIG,
      resp.data?.appConfigs as Array<{ group: string, key: string, value: string }>));
    if (callbackParams?.resetReload) {
      yield call(callbackParams.resetReload);
    }
    if (callbackParams?.configSuccess) {
      yield call(callbackParams.configSuccess, buttonManageEmail);
    }
  } else {
    if (callbackParams?.configSuccess) {
      yield call(callbackParams.configSuccess, buttonManageEmail);
    }
    if (callbackParams?.resetReload) {
      yield call(callbackParams.resetReload);
    }
  }
}

export const generalSagas = {
  initialLoad,
  callTnc,
  getTncHandler,
  postTncHandler,
  getGeneralConfigsHandler,
  callGeneralConfig,
  watchers: [
    takeLatest(GENERAL_ACTION_TYPE.INITIAL_LOAD, initialLoad),
    takeLatest(GENERAL_ACTION_TYPE.GET_CONFIG, callGeneralConfig)
  ]
};
